import { useQuery } from "@tanstack/react-query";
import { useDraftMode } from "../contexts/DraftModeContext";
import { backendService } from "../services/backend.service";
import type { WageBreakdownResult } from "../utils/models";
import { useDraftDayStats } from "./useDraftDayStats";

type UseShiftWageBreakdownResult = {
	data: WageBreakdownResult | null | undefined;
	isLoading: boolean;
	isError: boolean;
	error: unknown;
	refetch: () => void;
};

export const useShiftWageBreakdown = (shiftId: string | null, departmentId: string, isActual?: boolean, canViewWage = true): UseShiftWageBreakdownResult => {
	const { isDraftMode, activeDayData } = useDraftMode();
	const draftDateISO = activeDayData?.date.toFormat("yyyy-MM-dd");
	const draftStats = useDraftDayStats(isDraftMode ? draftDateISO : undefined);

	const liveQuery = useQuery<WageBreakdownResult | null>({
		queryKey: ["shift-wage-breakdown", shiftId, departmentId, isActual],
		queryFn: async () => {
			if (!shiftId) return null;
			const { data } = await backendService.getShiftWageBreakdown(shiftId, departmentId, isActual);
			return data;
		},
		enabled: !isDraftMode && !!shiftId && canViewWage,
	});

	if (isDraftMode) {
		const draftData: WageBreakdownResult | null = shiftId ? (draftStats.data?.shiftBreakdowns?.[shiftId] ?? null) : null;
		return {
			data: draftData,
			isLoading: draftStats.isLoading,
			isError: draftStats.isError,
			error: draftStats.error,
			refetch: () => {
				void draftStats.refetch();
			},
		};
	}
	return {
		data: liveQuery.data,
		isLoading: liveQuery.isLoading,
		isError: liveQuery.isError,
		error: liveQuery.error,
		refetch: () => {
			void liveQuery.refetch();
		},
	};
};
